import { FC } from 'react';

import { Box, Group, Image, Title } from '@mantine/core';

import { Container } from '@marketing/components/container/Container';

import { CompanyProfile, File } from 'models';
import { URLHelpers } from 'helpers/URLHelpers';

import { BoothNumber } from 'components/booth-number/BoothNumber';
import { HeaderLocation } from 'components/company-profile/components/HeaderLocation';
import { Logo } from 'components/company-profile-teaser/components/Logo';
import { IKImage } from 'components/ik-image/IKImage';
import { VerifiedBadge } from 'components/verified-badge/VerifiedBadge';
import { YouTubeVideo } from 'components/youtube-video/YouTubeVideo';
import { useCompanyCoverImage } from 'hooks/use-company-cover-image';
import { useIsMobile } from 'hooks/use-is-mobile';

import classes from './Header.module.scss';

const Header: FC<{
    company: CompanyProfile;
    actions?: React.ReactNode;
    headerButtons?: React.ReactNode;
}> = ({ company, actions, headerButtons }) => {
    const isMobile = useIsMobile('sm');

    const isVerified = !company.internal;
    const hasLogo = Boolean(company.logos.small || company.logos.large);

    return (
        <Box className={classes.cover}>
            <Cover company={company} />

            {actions && (
                <Box className={classes.cornerActions}>
                    <Container padding="x">
                        <Group gap="xs" justify="flex-end" className={classes.cornerActionsButtons}>
                            {actions}
                        </Group>
                    </Container>
                </Box>
            )}

            <Box className={classes.coverWrapper}>
                <Container padding="x">
                    <Box className={classes.coverContent}>
                        <Box className={classes.identity}>
                            {hasLogo && (
                                <Box className={classes.logo}>
                                    <Logo company={company} withBorder size={isMobile ? 64 : 140} />
                                </Box>
                            )}

                            <Box className={classes.nameBlock}>
                                <Title className={classes.name}>
                                    {company.name}
                                    {isVerified && (
                                        <VerifiedBadge
                                            compact
                                            withTooltip
                                            companyName={company.name}
                                            compactSize={isMobile ? 20 : 28}
                                            style={{ marginLeft: 8, verticalAlign: 'middle' }}
                                        />
                                    )}
                                </Title>
                                <Box className={classes.meta}>
                                    <Box className={classes.location}>
                                        <HeaderLocation company={company} />
                                    </Box>
                                    <BoothNumber showEventName company={company} style={{ flexShrink: 0 }} />
                                </Box>
                            </Box>
                        </Box>

                        {headerButtons && <Box className={classes.actions}>{headerButtons}</Box>}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

const Cover: FC<{ company: CompanyProfile }> = ({ company }) => {
    const { image, videoUrl, youTubeUrl, coverType } = useCompanyCoverImage(company);

    if (coverType === 'image' && image) {
        return (
            <IKImage
                loading="eager"
                fetchPriority="high"
                fileOrId={image as File}
                width={1440}
                height={1080}
                blurProps={{ enabled: true, belowDimensions: { width: 900, height: 600 } }}
                fallbackUrl={URLHelpers.file('/images/stock/energy-hero.jpg')}
            />
        );
    }

    if (coverType === 'video' && videoUrl) {
        return (
            <video
                src={videoUrl}
                width={1440}
                height={1080}
                autoPlay
                loop
                muted
                playsInline
                style={{ pointerEvents: 'none' }}
            />
        );
    }

    if (coverType === 'youtube' && youTubeUrl) {
        return <YouTubeVideo url={youTubeUrl} width={1440} height={1080} />;
    }

    return (
        <Image src={URLHelpers.file('/images/stock/solar-hero.jpg')} width={1440} height={1080} fetchPriority="high" />
    );
};

export { Header };
